'use client';

import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import {
  WAF_STATUS,
  toWafCheckableUrl,
  toWafStatusFromCheckOutcome,
  type WafStatusForDomain,
} from '@terra/ui-design-system';
import { wafCheckApi } from '../../clients/tester-client/waf-check-api';
import { APPLICATION_URL_WAF_STATUS_QUERY_KEY } from './useApplicationUrlWafStatus';

const WAF_STATUS_STALE_TIME_MS = 5 * 60 * 1000;

const resolveCheckableDomainUrl = (domain: string): string | undefined => {
  const trimmed = domain.trim();

  if (!trimmed) {
    return undefined;
  }

  const checkableUrl = toWafCheckableUrl(trimmed);

  return URL.canParse(checkableUrl) ? checkableUrl : undefined;
};

const toWafStatus = (
  result: UseQueryResult<WafStatusForDomain>,
): WafStatusForDomain => {
  if (result.isError) {
    return WAF_STATUS.error;
  }

  if (result.data) {
    return result.data;
  }

  return result.fetchStatus === 'idle' ? WAF_STATUS.idle : WAF_STATUS.pending;
};

const combineWafStatuses = (
  results: UseQueryResult<WafStatusForDomain>[],
): WafStatusForDomain[] => results.map(toWafStatus);

export function useDomainsWafStatuses({
  domains,
}: {
  domains: string[];
}): WafStatusForDomain[] {
  return useQueries({
    queries: domains.map(resolveCheckableDomainUrl).map((checkableUrl) => ({
      queryKey: [APPLICATION_URL_WAF_STATUS_QUERY_KEY, checkableUrl],
      queryFn: async () => {
        if (!checkableUrl) {
          throw new Error('Domain URL is required for a WAF check');
        }

        return toWafStatusFromCheckOutcome(
          await wafCheckApi.checkUrl({ url: checkableUrl }),
        );
      },
      enabled: Boolean(checkableUrl),
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: WAF_STATUS_STALE_TIME_MS,
    })),
    combine: combineWafStatuses,
  });
}
